import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { useSignalR } from "../../../hooks";
import { EditIssueDto, FetchError, GetIssuesDto, IssueDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";
import { optimisticallyUpdateQueryData } from "../utilities";

type Variables = {
  issueGuid: string;
  teamSlug?: string;
  description: string;
  createdByUserId: string;
};

const useEditIssueRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editIssueRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditIssueDto = {
      description: variables.description,
    };

    const response = await fetch(`${configuration.apiRootUrl}/issues/${variables.issueGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editIssueRequest };
};

const useEditIssue = () => {
  const { editIssueRequest } = useEditIssueRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editIssueRequest,
    onMutate: async (variables) => {
      let cachedTeamIssuesData: GetIssuesDto | undefined;
      let cachedUserIssuesData: GetIssuesDto | undefined;
      let cachedIssueDetailsData: IssueDto | undefined;

      if (variables.teamSlug) {
        cachedTeamIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
          queryClient,
          queryKeys.issues.getIssues(variables.teamSlug, undefined),
          (cachedData) => ({
            ...cachedData,
            issues: cachedData.issues.map((issue) =>
              issue.guid === variables.issueGuid
                ? {
                    ...issue,
                    description: variables.description,
                  }
                : issue,
            ),
          }),
        );
      }

      if (variables.createdByUserId) {
        cachedUserIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
          queryClient,
          queryKeys.issues.getIssues(undefined, variables.createdByUserId),
          (cachedData) => ({
            ...cachedData,
            issues: cachedData.issues.map((issue) =>
              issue.guid === variables.issueGuid
                ? {
                    ...issue,
                    description: variables.description,
                  }
                : issue,
            ),
          }),
        );
      }

      cachedIssueDetailsData = await optimisticallyUpdateQueryData<IssueDto>(
        queryClient,
        queryKeys.issues.getIssueDetail(variables.issueGuid),
        (cachedData) => ({
          ...cachedData,
          description: variables.description,
        }),
      );

      return { cachedTeamIssuesData, cachedUserIssuesData, cachedIssueDetailsData };
    },
    onError: (_error, variables, context) => {
      if (variables.teamSlug) {
        queryClient.setQueryData(
          queryKeys.issues.getIssues(variables.teamSlug, undefined),
          context?.cachedTeamIssuesData,
        );
      }
      if (variables.createdByUserId) {
        queryClient.setQueryData(
          queryKeys.issues.getIssues(undefined, variables.createdByUserId),
          context?.cachedUserIssuesData,
        );
      }
      queryClient.setQueryData(queryKeys.issues.getIssueDetail(variables.issueGuid), context?.cachedIssueDetailsData);
    },
    onSuccess: (_data, variables) => {
      if (variables.teamSlug) {
        signalR.sendRefetchTeamIssues(variables.teamSlug);
      }
      if (variables.createdByUserId) {
        signalR.sendRefetchUserIssues(variables.createdByUserId);
      }
      signalR.sendRefetchIssue(variables.issueGuid);
      signalR.sendRefetchComments(variables.issueGuid);
    },
    onSettled: (_data, _error, variables) => {
      if (variables.teamSlug) {
        queryClient.invalidateQueries(queryKeys.issues.getIssues(variables.teamSlug, undefined));
      }
      if (variables.createdByUserId) {
        queryClient.invalidateQueries(queryKeys.issues.getIssues(undefined, variables.createdByUserId));
      }
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(variables.issueGuid));
      queryClient.invalidateQueries(queryKeys.issues.getIssueFeed(variables.issueGuid));
    },
  });
};

export { useEditIssue };
